import { observer } from 'mobx-react';
import { AppraiserStore } from '../store/appraiserStore';
import {
  Card,
  createPhoneField,
  createSelectField,
  createTextField,
  FileUploadModal,
  IconButton,
  Paper,
} from '@roc/ui';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import React, { useState } from 'react';
import { PropertyAddressDetails } from '@roc/ui/formComponents';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Attachment,
  CloudDownload,
  CloudUpload,
  GetApp,
} from '@material-ui/icons';
import { AppraiserDocumentType } from '../utils/types';
import { DocumentName } from '@roc/feature-documents';
import { useUserStore } from '@roc/feature-app-core';

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    '& th': {
      borderBottom: '1px solid #eee',
    },
  },
  headerRow: {
    '& th': {
      verticalAlign: 'text-top',
      padding: '8px',
    },
    '& th:first-child': {
      paddingLeft: '16px',
    },
  },
  action: {
    cursor: 'pointer',
  },
}));

export type AddEditAppraiserProperties = {
  store: AppraiserStore;
};

export const AddEditAppraiser = observer(
  ({ store }: AddEditAppraiserProperties) => {
    const classes = useStyles();
    const { userStore } = useUserStore();
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [documentType, setDocumentType] = useState(null);

    return (
      <Grid container direction={'row'} spacing={4}>
        <Grid item xs={12}>
          <Paper>
            <Card
              title={'Appraiser Details'}
              cardContentProps={{
                style: {
                  // overflow: 'auto',
                  // padding: '0',
                  borderTop: '1px solid #E0E0E0FF',
                },
              }}
            >
              <Grid container justifyContent="center" spacing={4}>
                {field(({ store }) => {
                  return createTextField({
                    store: store,
                    label: 'Appraiser Name',
                    fieldName: 'name',
                    testId: 'name',
                    disabled: !userStore.isTechUser,
                  });
                })({ store: store.appraiserFormStore })}
                {field(({ store }) => {
                  return createTextField({
                    store: store,
                    label: 'Appraiser Firm',
                    fieldName: 'firm',
                    testId: 'firm',
                    disabled: !userStore.isTechUser,
                  });
                })({ store: store.appraiserFormStore })}
                {field(({ store }) => {
                  return createPhoneField({
                    store: store,
                    label: 'Phone Number',
                    fieldName: 'phone',
                    testId: 'phone',
                    disabled: !(userStore.isTechUser || userStore.isCommittee),
                  });
                })({ store: store.appraiserFormStore })}
                {field(({ store, options }) => {
                  return createSelectField({
                    store: store,
                    label: 'Status',
                    fieldName: 'status',
                    testId: 'status',
                    options: options,
                    disabled: !(userStore.isTechUser || userStore.isCommittee),
                  });
                })({
                  store: store.appraiserFormStore,
                  options: store.statusOptions,
                })}
                {field(({ store, md }) => {
                  return createTextField({
                    store: store,
                    label: 'Email',
                    fieldName: 'email',
                    testId: 'email',
                    disabled: !(userStore.isTechUser || userStore.isCommittee),
                  });
                })({
                  store: store.appraiserFormStore,
                  md: 12,
                })}
              </Grid>
            </Card>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <Card title={'Address'}>
              <Grid container justifyContent="center">
                <PropertyAddressDetails
                  store={store.appraiserFormStore}
                  disabled={!(userStore.isTechUser || userStore.isCommittee)}
                  showMap={false}
                />
              </Grid>
            </Card>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <Card
              title={'Supporting Documents'}
              cardContentProps={{
                style: {
                  borderTop: '1px solid #E0E0E0FF',
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow className={classes.headerRow}>
                    <TableCell>Document Name</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box display="flex">
                        <Typography>Appraiser Licence</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="center">
                        {store.appraiserLicense && (
                          <IconButton testId={'licensePresent'} disabled={true}>
                            <Attachment />
                          </IconButton>
                        )}
                        <IconButton
                          type="submit"
                          color="primary"
                          testId="uploadLicense"
                          onClick={() => {
                            setDocumentType(AppraiserDocumentType.LICENSE);
                            setUploadModalOpen(true);
                          }}
                          disabled={!(userStore.isTechUser || userStore.isCommittee)}
                        >
                          <CloudUpload />
                        </IconButton>
                        {store.currentAppraiser?.licenseDocumentPath && (
                          <IconButton
                            type="submit"
                            color="primary"
                            testId="downloadAppraiserLicense"
                            onClick={() =>
                              store.downloadDocument(
                                AppraiserDocumentType.LICENSE
                              )
                            }
                          >
                            <CloudDownload />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box display="flex">
                        <Typography>E&O Policy</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="center">
                        {store.appraiserPolicy && (
                          <IconButton testId={'licensePresent'} disabled={true}>
                            <Attachment />
                          </IconButton>
                        )}
                        <IconButton
                          type="submit"
                          color="primary"
                          testId="uploadPolicy"
                          onClick={() => {
                            setDocumentType(AppraiserDocumentType.POLICY);
                            setUploadModalOpen(true);
                          }}
                          disabled={!(userStore.isTechUser || userStore.isCommittee)}
                        >
                          <CloudUpload />
                        </IconButton>
                        {store.currentAppraiser?.policyDocumentPath && (
                          <IconButton
                            type="submit"
                            color="primary"
                            testId="downloadAppraiserPolicy"
                            onClick={() =>
                              store.downloadDocument(
                                AppraiserDocumentType.POLICY
                              )
                            }
                          >
                            <CloudDownload />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          </Paper>
        </Grid>
        <FileUploadModal
          open={isUploadModalOpen && documentType}
          title={`Upload ${
            documentType === AppraiserDocumentType.LICENSE
              ? DocumentName.APPRAISAL_LICENSE
              : documentType === AppraiserDocumentType.POLICY
              ? DocumentName.APPRAISAL_E_AND_O_POLICY
              : 'Document'
          }`}
          onClose={() => {
            setUploadModalOpen(false);
            setDocumentType(null);
          }}
          onUpload={documents => {
            switch (documentType) {
              case AppraiserDocumentType.LICENSE:
                store.appraiserLicense = documents[0];
                break;
              case AppraiserDocumentType.POLICY:
                store.appraiserPolicy = documents[0];
                break;
            }
          }}
        />
      </Grid>
    );
  }
);

const field = component => {
  const WrappedComponent = observer(component);
  return props => (
    <Grid item xs={props.xs ?? 12} md={props.md ?? 6}>
      <WrappedComponent {...props} />
    </Grid>
  );
};
